import {
    type ConnectedPosition,
    Overlay,
    OverlayModule,
    OverlayPositionBuilder,
    type OverlayRef,
} from '@angular/cdk/overlay'
import { TemplatePortal } from '@angular/cdk/portal'
import { isPlatformBrowser } from '@angular/common'
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    type OnDestroy,
    output,
    PLATFORM_ID,
    signal,
    type TemplateRef,
    viewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core'

import type { ClassValue } from 'clsx'

import { APP_DROPDOWN_OVERLAY_VIEWPORT_MARGIN } from '@core/constants/app.constants'
import { mergeClasses } from '@/styles/utils/merge-classes'

import { MENU_POSITIONS_MAP } from '../menu/menu-positions'
import { dropdownContentVariants } from './dropdown.variants'

@Component({
    selector: 'z-dropdown-menu',
    imports: [OverlayModule],
    template: `
        <!-- Dropdown Trigger -->
        <div
            class="contents"
            (click)="toggle()"
            (keydown.{enter,space}.prevent)="toggle()"
            tabindex="0"
        >
            <ng-content select="[dropdown-trigger]" />
        </div>

        <!-- Template for overlay content -->
        <ng-template #dropdownTemplate>
            <div
                [class]="contentClasses()"
                role="menu"
                [attr.data-state]="'open'"
                (keydown.{arrowdown,arrowup,enter,space,escape,home,end}.prevent)="
                    onDropdownKeydown($event)
                "
                tabindex="-1"
            >
                <ng-content />
            </div>
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'relative inline-block text-left',
        '[attr.data-state]': 'isOpen() ? "open" : "closed"',
        '(document:click)': 'onDocumentClick($event)',
    },
    exportAs: 'zDropdownMenu',
})
export class ZardDropdownMenuComponent implements OnDestroy {
    private elementRef = inject(ElementRef)
    private overlay = inject(Overlay)
    private overlayPositionBuilder = inject(OverlayPositionBuilder)
    private viewContainerRef = inject(ViewContainerRef)
    private platformId = inject(PLATFORM_ID)

    readonly dropdownTemplate = viewChild.required<TemplateRef<unknown>>('dropdownTemplate')

    private overlayRef?: OverlayRef
    private portal?: TemplatePortal
    private overlayItemClickListener?: () => void

    readonly class = input<ClassValue>('')
    /**
     * CDK often sizes the overlay pane to the trigger width; profile menus need a wider floor.
     * Defaults to 200px (existing behavior).
     */
    readonly overlayMinWidth = input<number>(200)
    readonly zAlign = input<'start' | 'end'>('start')
    readonly zPush = input(false, { transform: booleanAttribute })
    /** When false, the panel only opens below the trigger (no flip above). */
    readonly zFlip = input(true, { transform: booleanAttribute })
    readonly disabled = input(false, { transform: booleanAttribute })
    /** When true, scrolling closes the menu instead of repositioning the overlay with the trigger. */
    readonly closeOnScroll = input(false, { transform: booleanAttribute })

    readonly openChange = output<boolean>()

    readonly isOpen = signal(false)
    readonly focusedIndex = signal<number>(-1)

    protected readonly contentClasses = computed(() =>
        mergeClasses(dropdownContentVariants(), this.class()),
    )

    ngOnDestroy() {
        this.destroyOverlay()
    }

    onDocumentClick(event: Event) {
        const target = event.target as Node | null
        if (!target) {
            return
        }
        if (this.elementRef.nativeElement.contains(target)) {
            return
        }
        // Menu content is portaled to a CDK overlay (not under this host); ignore those clicks
        if (this.overlayRef?.hasAttached() && this.overlayRef.overlayElement.contains(target)) {
            return
        }
        this.close()
    }

    onDropdownKeydown(e: Event) {
        const items = this.getDropdownItems()
        const { key } = e as KeyboardEvent

        switch (key) {
            case 'ArrowDown':
                this.navigateItems(1, items)
                break
            case 'ArrowUp':
                this.navigateItems(-1, items)
                break
            case 'Enter':
            case ' ':
                this.selectFocusedItem(items)
                break
            case 'Escape':
                this.close()
                this.focusTrigger()
                break
            case 'Home':
                this.focusFirstItem(items)
                break
            case 'End':
                this.focusLastItem(items)
                break
        }
    }

    toggle() {
        if (this.disabled()) {
            return
        }
        if (this.isOpen()) {
            this.close()
        } else {
            this.open()
        }
    }

    open() {
        if (this.isOpen()) {
            return
        }

        if (!this.overlayRef) {
            this.createOverlay()
        }

        if (!this.overlayRef) {
            return
        }

        this.portal = new TemplatePortal(this.dropdownTemplate(), this.viewContainerRef)
        this.overlayRef.attach(this.portal)
        this.isOpen.set(true)
        this.openChange.emit(true)

        const onItemClick = (e: Event) => {
            const item = (e.target as Element).closest(
                'z-dropdown-menu-item, [z-dropdown-menu-item]',
            )
            if (item && !item.hasAttribute('data-disabled')) {
                this.close()
            }
        }
        this.overlayRef.overlayElement.addEventListener('click', onItemClick)
        this.overlayItemClickListener = () =>
            this.overlayRef?.overlayElement.removeEventListener('click', onItemClick)

        setTimeout(() => {
            this.focusDropdown()
            this.focusSelectedItemOrFirst(this.getDropdownItems())
        }, 0)
    }

    close() {
        this.overlayItemClickListener?.()
        this.overlayItemClickListener = undefined
        if (this.overlayRef?.hasAttached()) {
            this.overlayRef.detach()
        }
        this.isOpen.set(false)
        this.focusedIndex.set(-1)
        this.openChange.emit(false)
    }

    private createOverlay() {
        if (this.overlayRef) {
            return
        }

        if (isPlatformBrowser(this.platformId)) {
            try {
                const positionStrategy = this.overlayPositionBuilder
                    .flexibleConnectedTo(this.elementRef)
                    .withPositions(this.getOverlayPositions())
                    .withPush(this.zPush())
                    .withViewportMargin(APP_DROPDOWN_OVERLAY_VIEWPORT_MARGIN)

                this.overlayRef = this.overlay.create({
                    positionStrategy,
                    hasBackdrop: false,
                    scrollStrategy: this.closeOnScroll()
                        ? this.overlay.scrollStrategies.close()
                        : this.overlay.scrollStrategies.reposition(),
                    minWidth: this.overlayMinWidth(),
                    maxHeight: 400,
                })
            } catch (error) {
                console.error('Error creating overlay:', error)
            }
        }
    }

    private getOverlayPositions(): ConnectedPosition[] {
        const positions =
            this.zAlign() === 'end'
                ? MENU_POSITIONS_MAP['bottomRight']
                : MENU_POSITIONS_MAP['bottomLeft']
        // Prefer below trigger; optionally flip above when near viewport bottom.
        return this.zFlip() ? positions : [positions[0]]
    }

    private destroyOverlay() {
        if (this.overlayRef) {
            this.overlayRef.dispose()
            this.overlayRef = undefined
        }
    }

    private getDropdownItems(): HTMLElement[] {
        if (!this.overlayRef?.hasAttached()) {
            return []
        }
        const dropdownElement = this.overlayRef.overlayElement
        return Array.from(
            dropdownElement.querySelectorAll<HTMLElement>(
                'z-dropdown-menu-item, [z-dropdown-menu-item]',
            ),
        ).filter((item) => item.dataset['disabled'] === undefined)
    }

    private navigateItems(direction: number, items: HTMLElement[]) {
        if (items.length === 0) {
            return
        }

        const currentIndex = this.focusedIndex()
        let nextIndex = currentIndex + direction

        if (nextIndex < 0) {
            nextIndex = items.length - 1
        } else if (nextIndex >= items.length) {
            nextIndex = 0
        }

        this.focusedIndex.set(nextIndex)
        this.updateItemFocus(items, nextIndex)
    }

    private selectFocusedItem(items: HTMLElement[]) {
        const currentIndex = this.focusedIndex()
        if (currentIndex >= 0 && currentIndex < items.length) {
            const item = items[currentIndex]
            item.click()
        }
    }

    private focusFirstItem(items: HTMLElement[]) {
        if (items.length > 0) {
            this.focusedIndex.set(0)
            this.updateItemFocus(items, 0)
        }
    }

    private focusSelectedItemOrFirst(items: HTMLElement[]) {
        if (!items?.length) {
            return
        }
        const selectedIndex = items.findIndex((item) => item.hasAttribute('data-selected'))
        if (selectedIndex >= 0) {
            this.focusedIndex.set(selectedIndex)
            this.updateItemFocus(items, selectedIndex)
            return
        }
        this.focusFirstItem(items)
    }

    private focusLastItem(items: HTMLElement[]) {
        if (items.length > 0) {
            const lastIndex = items.length - 1
            this.focusedIndex.set(lastIndex)
            this.updateItemFocus(items, lastIndex)
        }
    }

    private updateItemFocus(items: HTMLElement[], focusedIndex: number) {
        items.forEach((item, index) => {
            if (index === focusedIndex) {
                item.focus()
                item.setAttribute('data-highlighted', '')
            } else {
                item.removeAttribute('data-highlighted')
            }
        })
    }

    private focusDropdown() {
        if (this.overlayRef?.hasAttached()) {
            const dropdownElement = this.overlayRef.overlayElement.querySelector(
                '[role="menu"]',
            ) as HTMLElement
            if (dropdownElement) {
                dropdownElement.focus()
            }
        }
    }

    private focusTrigger() {
        const trigger = this.elementRef.nativeElement.querySelector('[dropdown-trigger], button, a')
        if (trigger) {
            trigger.focus()
        }
    }
}
